import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Box, Paper, Typography, TextField, IconButton,
  Avatar, Fab, Chip, Divider, Tooltip, useMediaQuery,
} from '@mui/material';
import {
  Close, Send, AutoAwesome, KeyboardArrowDown,
  DeleteSweep, ContentCopy, ThumbUp, ThumbDown,
  ArrowForward, Sms, Payments,
  VpnKey, CurrencyExchange, Check,
  SupportAgent,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme, alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';

const OPENAI_API_KEY = process.env.REACT_APP_OPENAI_API_KEY;

const SYSTEM_PROMPT = `You are Zuri, ZenoPay's intelligent and friendly AI assistant. ZenoPay is a leading African fintech platform based in Tanzania that empowers businesses and individuals with fast, secure digital financial services.

Your personality: warm, concise, proactive, and professional — like a knowledgeable friend who works in finance.

You assist users with:
• Expense Tracker: recording personal and business expenses, income, and products/services, and understanding profit
• Bulk SMS & Messaging: creating campaigns, 1-to-1 SMS, tracking sent messages
• Sender ID Management: registering and listing SMS sender IDs
• API Keys: generating, managing, and using ZenoPay API keys for developer integrations
• Webhook Events: setting up and managing webhook events for real-time notifications
• Currency Exchange: viewing exchange rates and performing foreign currency exchanges
• Virtual Cards: creating cardholders and managing virtual cards
• Account Management: password changes, PIN changes, business profile updates, registration

Guidelines:
- Be concise — 2-4 sentences unless the user needs more detail
- Use friendly tone with occasional emojis (but not excessive)
- For technical questions, give step-by-step guidance
- If you cannot answer something specific (like real-time balances), tell the user to check their dashboard
- Never fabricate specific account data, balances, or transaction details
- End responses with a helpful follow-up offer when appropriate`;

const GREETING = `Hi there! 👋 I'm **Zuri**, your ZenoPay AI assistant. I can help you with payments, SMS campaigns, API keys, and much more. What would you like to do today?`;

const CAPABILITIES = [
  { icon: Payments,            color: '#D32F2F', label: 'Expenses',       desc: 'Track spending & profit',   to: '/expenses',           q: 'How do I record an expense?' },
  { icon: Sms,                 color: '#6366F1', label: 'Bulk SMS',       desc: 'SMS campaigns & messaging', to: '/bulk-sms',           q: 'How do I send a bulk SMS campaign?' },
  { icon: VpnKey,              color: '#F59E0B', label: 'API Keys',       desc: 'Developer integrations',    to: '/api-key-list',       q: 'How do I generate and use an API key?' },
  { icon: CurrencyExchange,    color: '#3B82F6', label: 'Exchange',       desc: 'Currency rates & forex',    to: '/exchange-rates',     q: 'How do I check exchange rates?' },
  { icon: SupportAgent,        color: '#8B5CF6', label: 'Account Help',   desc: 'Profile & settings',        to: '/business-info',      q: 'How do I update my business profile?' },
];

const NAV_MAP = [
  { keywords: ['expense', 'expenses', 'income', 'product', 'profit', 'spending'],    label: 'Open Expenses',     icon: Payments,            to: '/expenses' },
  { keywords: ['sms', 'bulk sms', 'campaign', 'message', 'sender id'],               label: 'Go to SMS',         icon: Sms,                 to: '/bulk-sms' },
  { keywords: ['api key', 'api integration', 'developer', 'webhook', 'endpoint'],     label: 'API Keys',          icon: VpnKey,              to: '/api-key-list' },
  { keywords: ['exchange', 'currency', 'forex', 'rate'],                              label: 'Exchange Rates',    icon: CurrencyExchange,    to: '/exchange-rates' },
];

const QUICK_PROMPTS = [
  'How do I record an expense?',
  'How do I check exchange rates?',
  'How do I get an API key?',
  'What is ZenoPay?',
];

const readUserName = () => {
  try {
    const d = JSON.parse(localStorage.getItem('userData') || 'null');
    return (d?.user?.name || d?.name || '').split(' ')[0] || '';
  } catch { return ''; }
};

const formatTime = (ts) => {
  const d = new Date(ts);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

function getNavSuggestions(content) {
  if (!content) return [];
  const lower = content.toLowerCase();
  return NAV_MAP.filter(n => n.keywords.some(k => lower.includes(k))).slice(0, 2);
}

// ── Typing indicator ────────────────────────────────────────────────

function TypingDots() {
  return (
    <Box sx={{ display: 'flex', gap: 0.5, px: 1.5, py: 1.4, alignItems: 'center' }}>
      {[0, 1, 2].map(i => (
        <motion.div
          key={i}
          style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: '#0BA480' }}
          animate={{ y: [0, -5, 0], opacity: [0.5, 1, 0.5] }}
          transition={{ duration: 0.6, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
        />
      ))}
    </Box>
  );
}

// ── Welcome screen ──────────────────────────────────────────────────

function WelcomeScreen({ onCapabilityClick, onQuickPrompt, isDark, name }) {
  const theme = useTheme();
  return (
    <Box sx={{ flex: 1, overflowY: 'auto', px: 2, py: 2.5, '&::-webkit-scrollbar': { width: 3 }, '&::-webkit-scrollbar-thumb': { bgcolor: theme.palette.divider, borderRadius: 4 } }}>

      {/* Greeting */}
      <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }}>
        <Box sx={{ textAlign: 'center', mb: 3 }}>
          <Box sx={{ width: 64, height: 64, borderRadius: '20px', background: 'linear-gradient(135deg, #0BA480 0%, #047857 100%)', display: 'flex', alignItems: 'center', justifyContent: 'center', mx: 'auto', mb: 1.5, boxShadow: '0 8px 24px rgba(11,164,128,0.3)' }}>
            <AutoAwesome sx={{ fontSize: 28, color: '#fff' }} />
          </Box>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: isDark ? '#F1F5F9' : '#0F172A', letterSpacing: '-0.02em' }}>
            {name ? `Hi ${name}! 👋` : 'Hi there! 👋'}
          </Typography>
          <Typography sx={{ fontSize: 13, color: isDark ? '#94A3B8' : '#64748B', mt: 0.5 }}>
            I'm Zuri, your ZenoPay AI assistant
          </Typography>
        </Box>
      </motion.div>

      {/* Capabilities grid */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.1 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1.25 }}>
          What I can help with
        </Typography>
        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, mb: 2.5 }}>
          {CAPABILITIES.map(cap => (
            <Box
              key={cap.label}
              onClick={() => onCapabilityClick(cap)}
              sx={{
                p: 1.25, borderRadius: '12px', cursor: 'pointer',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E2E8F0'}`,
                bgcolor: isDark ? '#141414' : '#fff',
                display: 'flex', alignItems: 'center', gap: 1,
                transition: 'all 0.18s ease',
                '&:hover': {
                  borderColor: alpha(cap.color, 0.4),
                  bgcolor: alpha(cap.color, 0.05),
                  transform: 'translateY(-1px)',
                },
                '&:active': { transform: 'scale(0.97)' },
              }}
            >
              <Box sx={{ width: 32, height: 32, borderRadius: '9px', bgcolor: alpha(cap.color, 0.12), display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                <cap.icon sx={{ fontSize: 16, color: cap.color }} />
              </Box>
              <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontSize: 12, fontWeight: 700, color: isDark ? '#F1F5F9' : '#0F172A', lineHeight: 1.2 }}>
                  {cap.label}
                </Typography>
                <Typography sx={{ fontSize: 10, color: isDark ? '#64748B' : '#94A3B8', mt: 0.2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {cap.desc}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </motion.div>

      {/* Quick prompts */}
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35, delay: 0.2 }}>
        <Typography sx={{ fontSize: 11, fontWeight: 600, color: isDark ? '#64748B' : '#94A3B8', textTransform: 'uppercase', letterSpacing: 0.8, mb: 1 }}>
          Quick questions
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {QUICK_PROMPTS.map(q => (
            <Box
              key={q}
              onClick={() => onQuickPrompt(q)}
              sx={{
                px: 1.5, py: 1, borderRadius: '10px', cursor: 'pointer',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.07)' : '#E2E8F0'}`,
                bgcolor: isDark ? '#141414' : '#fff',
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                transition: 'all 0.18s ease',
                '&:hover': { borderColor: alpha('#0BA480', 0.35), bgcolor: alpha('#0BA480', 0.04) },
              }}
            >
              <Typography sx={{ fontSize: 13, color: isDark ? '#CBD5E1' : '#334155' }}>{q}</Typography>
              <ArrowForward sx={{ fontSize: 14, color: '#0BA480', opacity: 0.7 }} />
            </Box>
          ))}
        </Box>
      </motion.div>
    </Box>
  );
}

// ── Single message bubble ────────────────────────────────────────────

function MessageBubble({ msg, isLast, onNav, isDark }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const [copied, setCopied] = useState(false);
  const [reaction, setReaction] = useState(null);
  const isUser = msg.role === 'user';
  const navSuggestions = (!isUser && !msg.streaming && isLast) ? getNavSuggestions(msg.content) : [];

  const handleCopy = () => {
    navigator.clipboard.writeText(msg.content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const renderContent = (text) => {
    // Bold **text** → render as bold spans
    const parts = text.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) =>
      part.startsWith('**') && part.endsWith('**')
        ? <strong key={i}>{part.slice(2, -2)}</strong>
        : part
    );
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.22 }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start', mb: 0.5 }}>

        {/* Bubble row */}
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.75, maxWidth: '82%', position: 'relative' }}>
          {!isUser && (
            <Avatar sx={{ width: 26, height: 26, fontSize: 11, fontWeight: 800, background: 'linear-gradient(135deg, #0BA480, #047857)', flexShrink: 0, mb: 0.25 }}>
              Z
            </Avatar>
          )}

          <Box
            sx={{
              px: 1.5, py: 1,
              borderRadius: isUser ? '16px 16px 4px 16px' : '4px 16px 16px 16px',
              bgcolor: isUser ? '#0BA480' : (isDark ? '#1a1a1a' : '#fff'),
              color: isUser ? '#fff' : (isDark ? '#F1F5F9' : '#0F172A'),
              border: !isUser ? `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}` : 'none',
              boxShadow: isUser ? '0 2px 12px rgba(11,164,128,0.25)' : (isDark ? 'none' : '0 1px 4px rgba(0,0,0,0.06)'),
              position: 'relative',
            }}
          >
            <Typography sx={{ fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
              {msg.streaming && msg.content === '' ? (
                <TypingDots />
              ) : (
                renderContent(msg.content)
              )}
              {msg.streaming && msg.content !== '' && (
                <motion.span
                  animate={{ opacity: [1, 0] }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  style={{ display: 'inline-block', width: 2, height: 14, bgcolor: '#0BA480', marginLeft: 2, verticalAlign: 'middle', borderRadius: 1 }}
                >▍</motion.span>
              )}
            </Typography>
          </Box>

          {/* Copy button — AI messages only */}
          {!isUser && !msg.streaming && (
            <Tooltip title={copied ? 'Copied!' : 'Copy'} placement="top">
              <IconButton
                size="small"
                onClick={handleCopy}
                sx={{ opacity: 0, position: 'absolute', right: -30, top: 4, width: 24, height: 24, bgcolor: isDark ? '#1e293b' : '#F1F5F9', border: `1px solid ${isDark ? 'rgba(255,255,255,0.1)' : '#E2E8F0'}`, color: isDark ? '#94A3B8' : '#64748B', '.MuiBubble:hover &': { opacity: 1 }, transition: 'opacity 0.18s',
                  '&:hover': { color: '#0BA480' } }}
              >
                {copied ? <Check sx={{ fontSize: 13 }} /> : <ContentCopy sx={{ fontSize: 13 }} />}
              </IconButton>
            </Tooltip>
          )}
        </Box>

        {/* Timestamp */}
        {msg.timestamp && (
          <Typography sx={{ fontSize: 10, color: isDark ? '#4B5563' : '#94A3B8', mt: 0.35, px: 0.5 }}>
            {formatTime(msg.timestamp)}
          </Typography>
        )}

        {/* Reactions + copy row for AI messages */}
        {!isUser && !msg.streaming && isLast && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5, px: 0.25 }}>
            <Tooltip title="Copy response">
              <IconButton size="small" onClick={handleCopy} sx={{ width: 22, height: 22, color: copied ? '#0BA480' : (isDark ? '#4B5563' : '#94A3B8'), '&:hover': { color: '#0BA480' } }}>
                {copied ? <Check sx={{ fontSize: 12 }} /> : <ContentCopy sx={{ fontSize: 12 }} />}
              </IconButton>
            </Tooltip>
            <Tooltip title="Helpful">
              <IconButton size="small" onClick={() => setReaction('up')} sx={{ width: 22, height: 22, color: reaction === 'up' ? '#0BA480' : (isDark ? '#4B5563' : '#94A3B8'), '&:hover': { color: '#0BA480' } }}>
                <ThumbUp sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
            <Tooltip title="Not helpful">
              <IconButton size="small" onClick={() => setReaction('down')} sx={{ width: 22, height: 22, color: reaction === 'down' ? '#ef4444' : (isDark ? '#4B5563' : '#94A3B8'), '&:hover': { color: '#ef4444' } }}>
                <ThumbDown sx={{ fontSize: 12 }} />
              </IconButton>
            </Tooltip>
            {reaction && (
              <Typography sx={{ fontSize: 10, color: reaction === 'up' ? '#0BA480' : '#ef4444', ml: 0.25 }}>
                {reaction === 'up' ? 'Thanks! 😊' : 'Got it, I\'ll improve!'}
              </Typography>
            )}
          </Box>
        )}

        {/* Navigation suggestions */}
        {navSuggestions.length > 0 && (
          <Box sx={{ display: 'flex', gap: 0.75, mt: 1, flexWrap: 'wrap' }}>
            {navSuggestions.map(nav => (
              <Chip
                key={nav.to}
                icon={<nav.icon sx={{ fontSize: '13px !important', color: '#0BA480 !important' }} />}
                label={nav.label}
                size="small"
                onClick={() => navigate(nav.to)}
                sx={{
                  fontSize: 11, fontWeight: 600, height: 26, cursor: 'pointer',
                  bgcolor: alpha('#0BA480', 0.08),
                  color: '#0BA480',
                  border: `1px solid ${alpha('#0BA480', 0.25)}`,
                  borderRadius: '8px',
                  '&:hover': { bgcolor: alpha('#0BA480', 0.15) },
                }}
              />
            ))}
          </Box>
        )}
      </Box>
    </motion.div>
  );
}

// ── Main component ──────────────────────────────────────────────────

export default function ZuriAI() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [open, setOpen] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [streaming, setStreaming] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const [showWelcome, setShowWelcome] = useState(true);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const userName = readUserName();

  useEffect(() => {
    if (open && !minimized) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
      setHasUnread(false);
      if (!showWelcome) setTimeout(() => inputRef.current?.focus(), 250);
    }
  }, [open, minimized, messages, showWelcome]);

  const startChat = useCallback((question) => {
    setShowWelcome(false);
    setTimeout(() => {
      streamMessage(question);
      setTimeout(() => inputRef.current?.focus(), 300);
    }, 50);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const streamMessage = useCallback(async (text) => {
    const userMsg = (text || input).trim();
    if (!userMsg || streaming) return;
    setInput('');

    const userEntry = { role: 'user', content: userMsg, timestamp: Date.now() };
    const history = [...messages, userEntry];
    setMessages([...history, { role: 'assistant', content: '', streaming: true, timestamp: Date.now() }]);
    setStreaming(true);

    const controller = new AbortController();
    abortRef.current = controller;

    try {
      const res = await fetch('https://api.openai.com/v1/chat/completions', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${OPENAI_API_KEY}`,
          'Content-Type': 'application/json',
        },
        signal: controller.signal,
        body: JSON.stringify({
          model: 'gpt-4o-mini',
          messages: [{ role: 'system', content: SYSTEM_PROMPT }, ...history],
          max_tokens: 600,
          temperature: 0.72,
          stream: true,
        }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';

      // eslint-disable-next-line no-constant-condition
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        const lines = buf.split('\n');
        buf = lines.pop() || '';

        for (const line of lines) {
          const trimmed = line.trim();
          if (!trimmed.startsWith('data: ')) continue;
          const data = trimmed.slice(6);
          if (data === '[DONE]') break;
          try {
            const delta = JSON.parse(data).choices?.[0]?.delta?.content || '';
            if (delta) {
              setMessages(prev => {
                const next = [...prev];
                const last = next[next.length - 1];
                if (last?.streaming) next[next.length - 1] = { ...last, content: last.content + delta };
                return next;
              });
            }
          } catch {}
        }
      }

      setMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.streaming) next[next.length - 1] = { ...last, streaming: false };
        return next;
      });
      if (!open || minimized) setHasUnread(true);
    } catch (err) {
      if (err.name === 'AbortError') return;
      setMessages(prev => {
        const next = [...prev];
        const last = next[next.length - 1];
        if (last?.streaming) {
          next[next.length - 1] = {
            role: 'assistant',
            content: "Sorry, I couldn't connect right now. Please check your connection and try again. 🔌",
            streaming: false,
            timestamp: Date.now(),
            error: true,
          };
        }
        return next;
      });
    } finally {
      setStreaming(false);
    }
  }, [input, messages, streaming, open, minimized]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      streamMessage();
    }
  };

  const clearChat = () => {
    abortRef.current?.abort();
    setMessages([]);
    setShowWelcome(true);
    setStreaming(false);
  };

  const panelWidth = isMobile ? 'calc(100vw - 16px)' : 400;
  const panelHeight = isMobile ? '82vh' : 580;
  const panelBottom = isMobile ? 8 : 28;
  const panelRight = isMobile ? 8 : 28;

  return (
    <>
      {/* ── FAB ─────────────────────────────────────────────────── */}
      <AnimatePresence>
        {!open && (
          <motion.div
            key="fab"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0, opacity: 0 }}
            transition={{ type: 'spring', stiffness: 340, damping: 24 }}
            style={{ position: 'fixed', bottom: panelBottom, right: panelRight, zIndex: 1400 }}
          >
            <Box sx={{ position: 'relative' }}>
              <Fab
                onClick={() => { setOpen(true); setMinimized(false); }}
                sx={{
                  width: 60, height: 60,
                  background: 'linear-gradient(135deg, #0BA480 0%, #059669 100%)',
                  color: '#fff',
                  boxShadow: '0 8px 32px rgba(11,164,128,0.45)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                    boxShadow: '0 12px 40px rgba(11,164,128,0.55)',
                    transform: 'translateY(-2px)',
                  },
                  transition: 'all 0.22s ease',
                }}
              >
                <motion.div
                  animate={{ rotate: [0, -10, 10, 0] }}
                  transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 5 }}
                >
                  <AutoAwesome sx={{ fontSize: 26 }} />
                </motion.div>
              </Fab>

              {/* Tooltip label */}
              <Box sx={{
                position: 'absolute', right: 68, top: '50%', transform: 'translateY(-50%)',
                bgcolor: isDark ? '#1e293b' : '#0F172A', color: '#fff',
                px: 1.5, py: 0.6, borderRadius: '8px',
                whiteSpace: 'nowrap', fontSize: 12, fontWeight: 600,
                pointerEvents: 'none',
                '&::after': { content: '""', position: 'absolute', right: -5, top: '50%', transform: 'translateY(-50%)', border: '5px solid transparent', borderLeftColor: isDark ? '#1e293b' : '#0F172A' },
              }}>
                Chat with Zuri
              </Box>

              {/* Unread badge */}
              <AnimatePresence>
                {hasUnread && (
                  <motion.div
                    initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ scale: 0 }}
                    style={{ position: 'absolute', top: 4, right: 4, width: 14, height: 14, borderRadius: '50%', backgroundColor: '#ef4444', border: '2.5px solid white' }}
                  />
                )}
              </AnimatePresence>
            </Box>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Chat panel ──────────────────────────────────────────── */}
      <AnimatePresence>
        {open && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, scale: 0.88, y: 28 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.88, y: 28 }}
            transition={{ type: 'spring', stiffness: 320, damping: 28 }}
            style={{
              position: 'fixed', bottom: panelBottom, right: panelRight,
              zIndex: 1400, width: panelWidth,
            }}
          >
            <Paper
              elevation={0}
              sx={{
                borderRadius: isMobile ? '20px' : '18px',
                overflow: 'hidden',
                border: `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : '#E2E8F0'}`,
                boxShadow: isDark ? '0 32px 100px rgba(0,0,0,0.7)' : '0 32px 100px rgba(0,0,0,0.2)',
                display: 'flex',
                flexDirection: 'column',
                height: minimized ? 'auto' : panelHeight,
                transition: 'height 0.3s cubic-bezier(0.4,0,0.2,1)',
              }}
            >
              {/* ── Header ──────────────────────────────────────── */}
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #0BA480 0%, #047857 100%)',
                  px: 2, py: 1.5,
                  display: 'flex', alignItems: 'center', gap: 1.5,
                  flexShrink: 0, cursor: 'pointer', userSelect: 'none',
                }}
                onClick={() => setMinimized(v => !v)}
              >
                {/* Avatar with pulse */}
                <Box sx={{ position: 'relative', flexShrink: 0 }}>
                  <Avatar sx={{ width: 40, height: 40, fontWeight: 800, fontSize: 16, background: 'rgba(255,255,255,0.18)', border: '2px solid rgba(255,255,255,0.35)' }}>
                    Z
                  </Avatar>
                  <motion.div
                    animate={{ scale: [1, 1.4, 1], opacity: [0.6, 0, 0.6] }}
                    transition={{ duration: 2, repeat: Infinity }}
                    style={{ position: 'absolute', inset: -2, borderRadius: '50%', border: '2px solid rgba(255,255,255,0.4)' }}
                  />
                  <Box sx={{ position: 'absolute', bottom: 1, right: 1, width: 10, height: 10, borderRadius: '50%', bgcolor: '#86efac', border: '2px solid rgba(255,255,255,0.8)' }} />
                </Box>

                {/* Name + status */}
                <Box sx={{ flex: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                    <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 15, lineHeight: 1.2 }}>
                      Zuri AI
                    </Typography>
                    <Box sx={{ px: 0.75, py: 0.15, borderRadius: '4px', bgcolor: 'rgba(255,255,255,0.18)', border: '1px solid rgba(255,255,255,0.25)' }}>
                      <Typography sx={{ fontSize: 9, color: '#fff', fontWeight: 700, letterSpacing: 0.4 }}>PRO</Typography>
                    </Box>
                  </Box>
                  <Typography sx={{ color: 'rgba(255,255,255,0.78)', fontSize: 11 }}>
                    ZenoPay Assistant · Online
                  </Typography>
                </Box>

                {/* Header actions */}
                <Box sx={{ display: 'flex', gap: 0.25 }} onClick={e => e.stopPropagation()}>
                  <Tooltip title="Clear chat">
                    <IconButton size="small" onClick={clearChat} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }, borderRadius: '8px' }}>
                      <DeleteSweep sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={minimized ? 'Expand' : 'Minimize'}>
                    <IconButton size="small" onClick={() => setMinimized(v => !v)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }, borderRadius: '8px' }}>
                      <motion.div animate={{ rotate: minimized ? 180 : 0 }} transition={{ duration: 0.22 }}>
                        <KeyboardArrowDown sx={{ fontSize: 18 }} />
                      </motion.div>
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Close">
                    <IconButton size="small" onClick={() => setOpen(false)} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fff', bgcolor: 'rgba(255,255,255,0.15)' }, borderRadius: '8px' }}>
                      <Close sx={{ fontSize: 18 }} />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              {/* ── Body (hidden when minimized) ──────────────────── */}
              <AnimatePresence>
                {!minimized && (
                  <motion.div
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ duration: 0.25 }}
                    style={{ display: 'flex', flexDirection: 'column', flex: 1, overflow: 'hidden' }}
                  >
                    {/* Welcome screen or messages */}
                    {showWelcome ? (
                      <WelcomeScreen
                        isDark={isDark}
                        name={userName}
                        onCapabilityClick={(cap) => startChat(cap.q)}
                        onQuickPrompt={(q) => startChat(q)}
                      />
                    ) : (
                      <Box
                        sx={{
                          flex: 1, overflowY: 'auto', px: 2, py: 2,
                          display: 'flex', flexDirection: 'column', gap: 1.5,
                          bgcolor: isDark ? '#0a0a0a' : '#F8FAFC',
                          '&::-webkit-scrollbar': { width: 3 },
                          '&::-webkit-scrollbar-thumb': { bgcolor: isDark ? 'rgba(255,255,255,0.1)' : '#E2E8F0', borderRadius: 4 },
                        }}
                      >
                        {messages.map((msg, i) => (
                          <MessageBubble
                            key={i}
                            msg={msg}
                            isLast={i === messages.length - 1}
                            isDark={isDark}
                          />
                        ))}
                        <div ref={messagesEndRef} />
                      </Box>
                    )}

                    <Divider />

                    {/* ── Input area ──────────────────────────────── */}
                    <Box
                      sx={{
                        px: 1.5, py: 1.25,
                        display: 'flex', gap: 1, alignItems: 'flex-end',
                        bgcolor: isDark ? '#141414' : '#fff',
                        flexShrink: 0,
                      }}
                    >
                      <TextField
                        inputRef={inputRef}
                        value={input}
                        onChange={(e) => setInput(e.target.value)}
                        onKeyDown={handleKeyDown}
                        placeholder="Ask Zuri anything…"
                        multiline
                        maxRows={4}
                        fullWidth
                        size="small"
                        disabled={streaming}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: '12px',
                            fontSize: 13,
                            bgcolor: isDark ? '#0a0a0a' : '#F8FAFC',
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => showWelcome ? setShowWelcome(false) : streamMessage()}
                        disabled={!input.trim() && !showWelcome}
                        sx={{
                          width: 40, height: 40, borderRadius: '12px', flexShrink: 0,
                          bgcolor: input.trim() ? '#0BA480' : (isDark ? '#1a1a1a' : '#F1F5F9'),
                          color: input.trim() ? '#fff' : (isDark ? '#4B5563' : '#94A3B8'),
                          '&:hover': { bgcolor: input.trim() ? '#059669' : undefined },
                          '&.Mui-disabled': { bgcolor: isDark ? '#1a1a1a' : '#F1F5F9', color: isDark ? '#374151' : '#CBD5E1' },
                          transition: 'all 0.2s ease',
                        }}
                      >
                        {streaming ? (
                          <motion.div animate={{ rotate: 360 }} transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}>
                            <AutoAwesome sx={{ fontSize: 18 }} />
                          </motion.div>
                        ) : (
                          <Send sx={{ fontSize: 18 }} />
                        )}
                      </IconButton>
                    </Box>

                    {/* Footer */}
                    <Box sx={{ textAlign: 'center', pb: 1, bgcolor: isDark ? '#141414' : '#fff' }}>
                      <Typography sx={{ fontSize: 10, color: isDark ? '#374151' : '#CBD5E1' }}>
                        Powered by <strong>Zuri AI</strong> · ZenoPay
                      </Typography>
                    </Box>
                  </motion.div>
                )}
              </AnimatePresence>
            </Paper>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
